/**
 * useGetGamificationModel — Fetch a GamificationModel by ID
 *
 * Public endpoint: GET /gamification-models/:id
 * Returns model metadata, config, state, milestones, and social proof counts.
 * Excludes DRAFT and ARCHIVED models.
 *
 * When authenticated, the response includes per-user `participation` state
 * (e.g., completedTileIds for BINGO). Auth state is included in the query key
 * so the cache invalidates on login/logout.
 */
import {useQuery, keepPreviousData} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {GamificationApi} from "@devour/client";

export {
    type GamificationModelResponse,
    type GamificationMilestone,
    type GamificationEligibility,
    type GamificationOutputConnection,
    type GamificationShareTemplate,
    type GamificationParticipationResponse,
} from "@devour/client";

export const GAMIFICATION_MODEL_QUERY_KEY = "gamification-model";

export function useGetGamificationModel(modelId?: string) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId, Boolean(fullToken?.token)],
        queryFn: () => new GamificationApi(getConfig(fullToken)).getGamificationModelsId({id: modelId!}),
        staleTime: 30_000,
        enabled: Boolean(modelId),
        placeholderData: keepPreviousData,
    });
}
